import { differenceInWeeks, format } from "date-fns";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Icon, IconButton, Typography } from "@/components/atoms";
import { useOperator, useProfile } from "@/hooks";
import { useLazyRequestVehicleDocumentQuery } from "@/redux/apis/document/documentApi";
import { downloadFile } from "@/utils";
import { getInsuranceStatus } from "../../MyVehicles/helpers";
import { VehicleDetailCard } from "../common/VehicleDetailCard";
import { useViewVehicleContext } from "../context";

export const VehicleInsuranceDetailCard = () => {
  const navigate = useNavigate();
  const { id: operatorId } = useOperator();
  const { dateFormat } = useProfile();
  const { vehicle, isLegacy } = useViewVehicleContext();
  const [requestDocument, { isLoading }] = useLazyRequestVehicleDocumentQuery();

  const status = getInsuranceStatus(vehicle);
  const insuranceDoc = vehicle.documents.find((doc) => doc.documentType === "INSURANCE");
  const isAffiliateVehicle = vehicle.owner.uuid !== operatorId;
  const isActionsDisabled = vehicle.metadata.isArchived || isAffiliateVehicle || isLegacy;

  const tooltip = useMemo(() => {
    if (!insuranceDoc || !insuranceDoc.expiryDate)
      return <Typography className="leading-tight">Missing vehicle insurance document.</Typography>;

    switch (status) {
      case "expiring": {
        const expiryDate = new Date(insuranceDoc.expiryDate);
        const difference = differenceInWeeks(expiryDate, new Date(), {
          roundingMethod: "ceil",
        });

        return (
          <Typography className="leading-tight">
            Vehicle Insurance Document is expiring in {difference} {difference === 1 ? "week" : "weeks"}, please update your document.
          </Typography>
        );
      }

      case "expired":
        return <Typography className="leading-tight">Vehicle Insurance Document has expired, please update your document.</Typography>;

      case "missing-details":
      case "unknown":
        return <Typography className="leading-tight">Missing vehicle insurance document.</Typography>;

      case "verified":
      default:
        return undefined;
    }
  }, [insuranceDoc, status]);

  const handleDownload = () => {
    if (!insuranceDoc) return;

    requestDocument({
      id: insuranceDoc.uuid,
      fileType: "pdf", //todo: get file type from document
      vehicleId: vehicle.uuid,
    })
      .unwrap()
      .then((res) => {
        downloadFile(res.filename, res.uuid);
      });
  };

  const DownloadButton = () => {
    return (
      <IconButton
        disabled={isLoading}
        iconName="ImportCurve"
        className="!bg-transparent p-1.5 text-neutral-black"
        iconSize="lg"
        variant="tertiary"
        onClick={handleDownload}
      />
    );
  };

  const ActionButton = () => {
    const showEditButton = !isActionsDisabled && (status === "expiring" || status === "expired" || status === "missing-details" || status === "unknown");

    return (
      <span className="flex items-center gap-1">
        {insuranceDoc && <DownloadButton />}
        {showEditButton && (
          <IconButton onClick={() => navigate("./edit")} iconName="Refresh2" variant="primary" size="sm" className="relative z-10" />
        )}
      </span>
    );
  };

  const description = insuranceDoc?.expiryDate
    ? `${status === "expired" ? "Expired" : "Expires"} on: ${format(new Date(insuranceDoc.expiryDate), dateFormat)}`
    : "N/A";

  return (
    <VehicleDetailCard
      title={
        <>
          Insurance
          <StatusBadge status={status} />
        </>
      }
      description={description}
      tooltip={tooltip}
      icon={<Icon name="file-pdf" isCustom size="xl" className="text-neutral-black" />}
      action={<ActionButton />}
      actionClick={handleDownload}
      status={status}
    />
  );
};

const StatusBadge = ({ status }: { status: string }) => {
  switch (status) {
    case "verified":
      return <Icon name="Verify" className="text-info" variant="Bold" size="md" />;
    case "expiring":
      return <Icon name="Danger" size="md" className="text-warning" variant="Bold" />;
    case "unknown":
    case "missing-details":
    case "expired":
      return <Icon name="InfoCircle" size="md" className=" text-danger" variant="Bold" />;
    default:
      return null;
  }
};
